import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import Meeting from "../../../models/Meeting";
import { authenticationService } from "../../../services/auth.service";
import api from "../../../services/api";
import toastOptions from "../../../utils/toastOptions";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { displayDateTime } from "../../../utils/masks";

import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import "./meeting-edit.css";
import {
  EditorState,
  ContentState,
  convertFromHTML,
} from "draft-js";
import { utils } from "../../../utils/utils";
import Editor from "../../../components/editor";
import DialogConfirm from "../../../components/DialogConfirm";
import IMeetingSignatureDTO from "../../../models/IMeetingSignatureDTO";

type MeetingParams = {
  id: string;
  idUser: string;
};

export default function MeetingSignature() {
  const params = useParams<MeetingParams>();
  const [isReadOnly, setReadOnly] = useState<boolean>(true);
  const [isRunningPrintAgenda, setRunningPrintAgenda] = useState<boolean>(false);
  const [isSigning, setIsSigning] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sign, setSign] = useState<boolean>(false);
  const [note, setNote] = useState<string>('');
  const navigate = useNavigate();

  const [meeting, setMeeting] = useState<Meeting>();
  const [signature, setSignature] = useState<IMeetingSignatureDTO>();

  const [editorState, setEditorState] = useState<EditorState>(
    EditorState.createEmpty()
  );

  useEffect(() => {
    const init = async () => {
      const id = +(params.id ?? 0);
      const idUser = +(params.idUser ?? 0);

      if (authenticationService.currentUserValue.id != idUser) {        
        toastOptions.onClose = () => {
          navigate('/');
        };

        toast.error("Você não pode assinar essa reunião", toastOptions);
        return;
      }

      if (id > 0 && authenticationService.currentUserValue.id == idUser) {
        await handleEdit(id);
      }

      setReadOnly(authenticationService.isReadOnly());
    };

    const handleEdit = async (id: number) => {
      try {
        setIsLoading(true);

        const { data } = await api.get(`meetings/${id}`);
        const { data: dataSignatures } = await api.get(`meetings/${id}/signatures`);

        setMeeting(data.item);

        setSignature(dataSignatures.item.filter((i: IMeetingSignatureDTO) => i.idUser == authenticationService.currentUserValue.id).shift());

        if (data.item.agenda) {
          const blocksFromHTML = convertFromHTML(data.item.agenda);
          if (blocksFromHTML) {
            const contentState = ContentState.createFromBlockArray(
              blocksFromHTML.contentBlocks,
              blocksFromHTML.entityMap
            );
            setEditorState(EditorState.createWithContent(contentState));
          }
        }
      } catch (e: any) {
        e.messages?.forEach((m: string) => {
          toast.error(m, toastOptions);
        });
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [params.id]);

  const handleSignature = async (confirmationCode: string) => {
    setSign(false);
    setIsSigning(true);
    try {
      await api.post(`/meetings/${meeting?.id}/signature`, { confirmationCode: confirmationCode, note: note });
      
      toastOptions.onClose = () => {
        setSign(false);
        navigate(`/reuniao/${meeting?.id}`);
      };
      toast.success("Reunião assinada com sucesso!", toastOptions);
    } catch (e: any) {
      setIsSigning(false);
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    }
  };

  const handleDownloadAgenda = async () => {
    setRunningPrintAgenda(true);
    try {
      if (meeting) {
        const { data } = await api.get(`meetings/${meeting?.id}/agenda/report`);
        const name = `${new Date(meeting.createdDate)
          .toLocaleDateString()
          .replaceAll("/", "-")}-ata-${new Date().getTime()}.pdf`;
        utils.printPDF(name, data.item);
      }
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.warning(m, toastOptions);
      });
    } finally {
      setRunningPrintAgenda(false);
    }
  };

  const dialogClose = () => {
    setSign(false);
  }

  return isLoading  ? (
    <div className="uk-flex uk-flex-center">
      <div uk-spinner="true; ratio:2">Carregando...</div>
    </div>
  ) : (
    <>
      <div id="page-sign-meeting">
        <div className="uk-modal-body">
          <h3>Assinatura Digital</h3>
          <div className="uk-grid-small" uk-grid="true">
            <div className="uk-width-1-2@s">
              <label className="uk-form-label" htmlFor="title">
                Título da Reunião
              </label>
              <textarea
                id="title"
                name="title"
                placeholder="Título"
                className="uk-textarea uk-form-small uk-text-uppercase"
                defaultValue={meeting?.title}
                style={{ resize: "none" }}
                disabled
                rows={2}
              />
            </div>
            <div className="uk-width-1-2@s">
              <div className="uk-grid-small" uk-grid="true">
                <div className="uk-width-1@s uk-text-right">
                  <label className="uk-margin-top">
                    Fechada em: {displayDateTime(meeting?.timeClose)}
                  </label>
                </div>
                <div className="uk-width-1@s uk-text-right">
                  <button
                    id="btn-print"
                    className="uk-button uk-button-primary uk-margin-left uk-button-small"
                    type="button"
                    disabled={isRunningPrintAgenda}
                    onClick={() => handleDownloadAgenda()}
                  >
                    {isRunningPrintAgenda ? (
                      <div uk-spinner="true; ratio:0.5"></div>
                    ) : (
                      <span
                        className="uk-margin-small-right"
                        uk-icon={`icon: download;`}
                      ></span>
                    )}
                    Baixar Ata
                  </button>
                </div>
              </div>
            </div>        
            <div className="uk-width-1-1@s">
              <Editor
                editorState={editorState}
                onEditorStateChange={(editorState: EditorState) =>
                  setEditorState(editorState)
                }
                wrapperClassName="wrapper-class"
                editorClassName="editor-class"
                toolbarClassName="toolbar-class"
                toolbarHidden={{
                  options: ["inline", "history"],
                  inline: { inDropdown: false, options: [] },
                  history: { inDropdown: true },
                }}
                readOnly
              />
            </div>
            <div className="uk-width-1-1@s">
              <label className="uk-form-label" htmlFor="note">Observações sobre a assinatura</label>
              <textarea
                id="note" 
                name="note"
                className="uk-textarea uk-form-small uk-text-uppercase" 
                rows={6} 
                style={{resize: 'none'}}
                onChange={(e:any) => setNote(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="uk-modal-footer">
          {
            signature?.signedAt && 
              <div className="uk-margin-bottom">
                <label className="uk-text-danger">
                  Você já assinou essa reunião em {displayDateTime(signature.signedAt)}, mas foi solicitada uma nova assintura
                </label>
              </div>
          }
          <div
            className="uk-grid-small uk-child-width-1-4@s uk-flex-center uk-text-center"
            uk-grid="true"
          >
            <Link
              to="/"
              className="uk-button uk-button-small uk-button-default uk-margin-left"
            >
              Dashboard
            </Link>
            <button
              id="btn-save"
              disabled={isLoading || isSigning || isReadOnly}
              className="uk-button uk-button-small uk-button-primary uk-margin-left"
              type="button"
              onClick={()=> setSign(true)}
            >
              {isSigning && <div uk-spinner="true; ratio:0.5"></div>}
              Assinar
            </button>
          </div>
        </div>
      </div>
      <DialogConfirm
          open={sign}
          text={[""]}
          title="Assine Digitalmente a Reunião"
          confirmTextLabel="Confirme a senha para assinar a reunião"
          handleConfirmation={handleSignature}
          afterClose={dialogClose}
      ></DialogConfirm>
    </>
  );
}
